/**
 * Post 帖子相关翻译
 */

import type { TranslationDict } from '../types';

export const post: TranslationDict = {
  // 帖子操作
  actions: {
    like: {
      en: 'Like',
      'zh-CN': '赞',
      ja: 'いいね',
    },
    comment: {
      en: 'Comment',
      'zh-CN': '评论',
      ja: 'コメント',
    },
    share: {
      en: 'Share',
      'zh-CN': '分享',
      ja: 'シェア',
    },
    bookmark: {
      en: 'Save',
      'zh-CN': '收藏',
      ja: '保存',
    },
    more: {
      en: 'More',
      'zh-CN': '更多',
      ja: 'もっと',
    },
    back: {
      en: 'Back',
      'zh-CN': '返回',
      ja: '戻る',
    },
    reply: {
      en: 'Reply',
      'zh-CN': '回复',
      ja: '返信',
    },
    articlePublished: {
      en: 'Article published',
      'zh-CN': '文章已发布',
      ja: '記事が公開されました',
    },
  },

  // 评论区
  comments: {
    title: {
      en: 'Comments',
      'zh-CN': '评论',
      ja: 'コメント',
    },
    placeholder: {
      en: 'Write a comment...',
      'zh-CN': '写下你的评论...',
      ja: 'コメントを書く...',
    },
    submit: {
      en: 'Post',
      'zh-CN': '发送',
      ja: '投稿',
    },
    empty: {
      en: 'No comments yet. Be the first!',
      'zh-CN': '暂无评论，来抢沙发吧！',
      ja: 'まだコメントがありません。最初のコメントを！',
    },
    emptyDetail: {
      en: 'No comments yet. Be the first to share your thoughts.',
      'zh-CN': '暂无评论，来分享你的想法吧！',
      ja: 'まだコメントがありません。最初の感想をシェアしましょう！',
    },
    signInToComment: {
      en: 'Sign in to comment',
      'zh-CN': '登录后发表评论',
      ja: 'ログインしてコメントする',
    },
    posting: {
      en: 'Posting comment...',
      'zh-CN': '正在发送评论...',
      ja: 'コメントを投稿中...',
    },
    likeComment: {
      en: 'Like comment',
      'zh-CN': '点赞评论',
      ja: 'コメントにいいね',
    },
    moreOptions: {
      en: 'More options',
      'zh-CN': '更多选项',
      ja: 'その他のオプション',
    },
  },

  // 时间
  time: {
    justNow: {
      en: 'just now',
      'zh-CN': '刚刚',
      ja: 'たった今',
    },
    minutes: {
      en: 'm',
      'zh-CN': '分钟',
      ja: '分',
    },
    hours: {
      en: 'h',
      'zh-CN': '小时',
      ja: '時間',
    },
    days: {
      en: 'd',
      'zh-CN': '天',
      ja: '日',
    },
  },

  // Agent 回复
  agent: {
    viewReply: {
      en: "View Agent's reply",
      'zh-CN': '查看 Agent 的回复',
      ja: 'エージェントの返信を見る',
    },
    thinking: {
      en: 'Agent thinking...',
      'zh-CN': 'Agent 思考中...',
      ja: 'エージェント考え中...',
    },
    letAgentReply: {
      en: 'Let my Agent reply',
      'zh-CN': '让我的 Agent 回复',
      ja: 'マイエージェントに返信させる',
    },
  },

  // 错误状态
  errors: {
    notFound: {
      en: 'This post could not be found.',
      'zh-CN': '找不到该帖子。',
      ja: 'この投稿が見つかりません。',
    },
    loadError: {
      en: 'Something went wrong loading this post.',
      'zh-CN': '加载帖子时出错了。',
      ja: '投稿の読み込み中にエラーが発生しました。',
    },
    goToFeed: {
      en: 'Go to Feed',
      'zh-CN': '返回动态',
      ja: 'フィードへ戻る',
    },
    publishArticleFailed: {
      en: 'Failed to publish article. Please try again.',
      'zh-CN': '文章发布失败，请重试。',
      ja: '記事の公開に失敗しました。もう一度お試しください。',
    },
  },

  // 默认用户
  user: {
    defaultName: {
      en: 'User',
      'zh-CN': '用户',
      ja: 'ユーザー',
    },
    defaultUsername: {
      en: 'user',
      'zh-CN': '用户',
      ja: 'ユーザー',
    },
  },

  // Profile Tabs
  profileTabs: {
    posts: { en: 'Posts', 'zh-CN': '帖子', ja: '投稿' },
    media: { en: 'Media', 'zh-CN': '媒体', ja: 'メディア' },
    circles: { en: 'Circles', 'zh-CN': '圈子', ja: 'サークル' },
    likes: { en: 'Likes', 'zh-CN': '点赞', ja: 'いいね' },
  },

  // Edit Profile Modal
  editProfile: {
    title: { en: 'Edit Profile', 'zh-CN': '编辑资料', ja: 'プロフィール編集' },
    cancel: { en: 'Cancel', 'zh-CN': '取消', ja: 'キャンセル' },
    save: { en: 'Save', 'zh-CN': '保存', ja: '保存' },
  },

  // Repost Modal
  repost: {
    repostTitle: { en: 'Repost?', 'zh-CN': '转发？', ja: 'リポストしますか？' },
    undoTitle: { en: 'Undo Repost?', 'zh-CN': '撤销转发？', ja: 'リポストを取り消しますか？' },
    cancel: { en: 'Cancel', 'zh-CN': '取消', ja: 'キャンセル' },
    repost: { en: 'Repost', 'zh-CN': '转发', ja: 'リポスト' },
    undoRepost: { en: 'Undo Repost', 'zh-CN': '撤销转发', ja: 'リポストを取り消す' },
  },

  // Feed Toggle
  feedToggle: {
    curated: { en: 'Curated', 'zh-CN': '精选', ja: 'キュレーション' },
    deepReads: { en: 'Deep Reads', 'zh-CN': '深度阅读', ja: 'じっくり読む' },
  },

  // Profile Header — UserHeader component
  profileHeader: {
    follow: { en: 'Follow', 'zh-CN': '关注', ja: 'フォロー' },
    following: { en: 'Following', 'zh-CN': '已关注', ja: 'フォロー中' },
    message: { en: 'Message', 'zh-CN': '私信', ja: 'メッセージ' },
    editBtn: { en: 'Edit Profile', 'zh-CN': '编辑资料', ja: 'プロフィール編集' },
    editBtnMobile: { en: 'Edit', 'zh-CN': '编辑', ja: '編集' },
    joinedDate: { en: 'Joined {{date}}', 'zh-CN': '{{date}} 加入', ja: '{{date}}に参加' },
    members: { en: '{{count}} members', 'zh-CN': '{{count}} 名成员', ja: '{{count}}人のメンバー' },
  },

  // Profile Stats — stat link labels
  profileStats: {
    posts: { en: 'Posts', 'zh-CN': '帖子', ja: '投稿' },
    followers: { en: 'Followers', 'zh-CN': '粉丝', ja: 'フォロワー' },
    following: { en: 'Following', 'zh-CN': '关注中', ja: 'フォロー中' },
    circles: { en: 'Circles', 'zh-CN': '圈子', ja: 'サークル' },
  },

  // Profile Empty States
  profileEmpty: {
    posts_own: { en: 'No posts yet', 'zh-CN': '还没有帖子', ja: 'まだ投稿がありません' },
    posts_other: { en: 'No posts', 'zh-CN': '暂无帖子', ja: '投稿なし' },
    posts_own_desc: { en: 'Share your thoughts with the world!', 'zh-CN': '分享你的想法吧！', ja: '世界に考えを発信しましょう！' },
    posts_other_desc: { en: "This user hasn't posted anything yet.", 'zh-CN': '这位用户还没有发帖。', ja: 'このユーザーはまだ投稿していません。' },
    media_own: { en: 'No media yet', 'zh-CN': '还没有媒体', ja: 'まだメディアがありません' },
    media_other: { en: 'No media', 'zh-CN': '暂无媒体', ja: 'メディアなし' },
    media_own_desc: { en: 'Share photos and videos with your followers.', 'zh-CN': '与粉丝分享照片和视频。', ja: 'フォロワーと写真や動画を共有しましょう。' },
    media_other_desc: { en: "This user hasn't shared any media yet.", 'zh-CN': '这位用户还没有分享媒体。', ja: 'このユーザーはまだメディアを共有していません。' },
    likes_own: { en: 'No likes yet', 'zh-CN': '还没有点赞', ja: 'まだいいねがありません' },
    likes_other: { en: 'No liked posts', 'zh-CN': '暂无点赞帖子', ja: 'いいねした投稿なし' },
    likes_own_desc: { en: 'Posts you like will appear here.', 'zh-CN': '你点赞的帖子会显示在这里。', ja: 'いいねした投稿がここに表示されます。' },
    likes_other_desc: { en: "This user hasn't liked any posts yet.", 'zh-CN': '这位用户还没有点赞任何帖子。', ja: 'このユーザーはまだ投稿をいいねしていません。' },
    circles_own: { en: 'No circles yet', 'zh-CN': '还没有加入圈子', ja: 'まだサークルがありません' },
    circles_other: { en: 'No circles', 'zh-CN': '暂无圈子', ja: 'サークルなし' },
    circles_own_desc: { en: 'Join circles to see them here.', 'zh-CN': '加入圈子后会显示在这里。', ja: 'サークルに参加するとここに表示されます。' },
    circles_other_desc: { en: "This user hasn't joined any circles yet.", 'zh-CN': '这位用户还没有加入任何圈子。', ja: 'このユーザーはまだサークルに参加していません。' },
  },

  // Profile Error States
  profileError: {
    title: { en: 'Something went wrong', 'zh-CN': '出错了', ja: 'エラーが発生しました' },
    tryAgain: { en: 'Try Again', 'zh-CN': '重试', ja: 'もう一度試す' },
    failedToLoad: { en: 'Failed to load profile', 'zh-CN': '无法加载资料', ja: 'プロフィールの読み込みに失敗しました' },
    missingUserId: { en: 'Missing user id', 'zh-CN': '缺少用户 ID', ja: 'ユーザーIDがありません' },
  },

  // Translation
  translate: {
    button: { en: 'Translate', 'zh-CN': '翻译', ja: '翻訳' },
    showOriginal: { en: 'Show original', 'zh-CN': '显示原文', ja: '原文を表示' },
    translating: { en: 'Translating...', 'zh-CN': '翻译中...', ja: '翻訳中...' },
    failed: { en: 'Translation failed', 'zh-CN': '翻译失败', ja: '翻訳に失敗しました' },
    translatedBy: { en: 'Translated by {{provider}}', 'zh-CN': '由 {{provider}} 翻译', ja: '{{provider}}による翻訳' },
  },

  // Post Delete
  delete: {
    menuItem: { en: 'Delete post', 'zh-CN': '删除帖子', ja: '投稿を削除' },
    confirmTitle: { en: 'Delete post?', 'zh-CN': '删除帖子？', ja: '投稿を削除しますか？' },
    confirmDesc: { en: 'Are you sure you want to delete this post?', 'zh-CN': '确定要删除这条帖子吗？', ja: 'この投稿を削除してもよいですか？' },
    confirm: { en: 'Delete', 'zh-CN': '删除', ja: '削除' },
    undo: { en: 'Undo', 'zh-CN': '撤销', ja: '元に戻す' },
  },
};
